import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { teacherService } from '@/services/teacher';
import type { AttendanceStatus } from '@/types';
import type {
  TeacherAttendanceTrendResponse,
  TeacherDashboardResponse,
  TeacherDashboardSubmission,
} from '@/types/teacher';
import { resolveUserPhoto } from '@/utils/avatars';

type DraftState = Record<string, AttendanceStatus>;
type SubmissionRequest = Array<{ studentId: string; status: AttendanceStatus }>;

const getInitialDraft = (submissions: TeacherDashboardSubmission[]): DraftState => {
  return submissions.reduce<DraftState>((acc, submission) => {
    acc[submission.studentId] = submission.status;
    return acc;
  }, {});
};

const buildPayload = (
  submissions: TeacherDashboardSubmission[],
  draft: DraftState,
): SubmissionRequest =>
  submissions.map((submission) => ({
    studentId: submission.studentId,
    status: draft[submission.studentId] ?? submission.status,
  }));

export default function TeacherDashboardPage() {
  const queryClient = useQueryClient();
  const [selectedClassId, setSelectedClassId] = useState<string | undefined>();
  const [draft, setDraft] = useState<DraftState>({});
  const [touchedStudentIds, setTouchedStudentIds] = useState<Set<string>>(new Set());
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  const {
    data: dashboard,
    isLoading,
    isError,
    error,
  } = useQuery<TeacherDashboardResponse>({
    queryKey: ['teacher-dashboard', selectedClassId],
    queryFn: () => teacherService.getDashboard(selectedClassId ? { classId: selectedClassId } : undefined),
  });

  const {
    data: attendanceTrend,
    isLoading: isTrendLoading,
  } = useQuery<TeacherAttendanceTrendResponse>({
    queryKey: ['teacher-attendance-trend', dashboard?.classId, 6],
    queryFn: () =>
      teacherService.getAttendanceTrend({
        months: 6,
        classId: dashboard?.classId ?? undefined,
      }),
    enabled: Boolean(dashboard?.classId),
    staleTime: 60_000,
  });

  const submissions = useMemo(() => dashboard?.submissions ?? [], [dashboard]);

  useEffect(() => {
    if (dashboard) {
      setDraft(getInitialDraft(dashboard.submissions));
      setTouchedStudentIds(new Set());
    }
  }, [dashboard]);

  const saveDraftMutation = useMutation<void, Error, SubmissionRequest>({
    mutationFn: async (payload) => {
      if (!dashboard?.classId || !dashboard?.date) return;
      await teacherService.saveDraft({
        date: dashboard.date,
        classId: dashboard.classId,
        attendance: payload,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teacher-dashboard', selectedClassId] });
      setTouchedStudentIds(new Set());
      setStatusMessage({ type: 'success', text: 'Draft saved successfully' });
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err?.message || 'Failed to save draft' });
    },
  });

  const submitMutation = useMutation<void, Error, SubmissionRequest>({
    mutationFn: async (payload) => {
      if (!dashboard?.classId || !dashboard?.date) return;
      await teacherService.submitAttendance({
        date: dashboard.date,
        classId: dashboard.classId,
        attendance: payload,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teacher-dashboard', selectedClassId] });
      setTouchedStudentIds(new Set());
      setStatusMessage({ type: 'success', text: 'Attendance submitted to school office' });
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err?.message || 'Failed to submit attendance' });
    },
  });

  const hasChanges = useMemo(() => {
    if (!dashboard) return false;
    return dashboard.submissions.some((submission) => {
      const selected = draft[submission.studentId];
      return selected && selected !== submission.status;
    });
  }, [dashboard, draft]);

  const totals = useMemo(() => {
    if (!dashboard || !submissions.length) {
      return { present: 0, absent: 0, late: 0, leave: 0, total: 0, rate: 0 };
    }
    const counts = submissions.reduce(
      (acc, submission) => {
        const val = draft[submission.studentId] ?? submission.status;
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      },
      { present: 0, absent: 0, late: 0, leave: 0 } as Record<AttendanceStatus, number>,
    );
    const total = submissions.length;
    const rate = total > 0 ? Math.round((counts.present / total) * 1000) / 10 : 0;
    return { ...counts, total, rate };
  }, [dashboard, submissions, draft]);

  const handleSetStatus = (studentId: string, status: AttendanceStatus) => {
    setDraft((prev) => ({ ...prev, [studentId]: status }));
    setTouchedStudentIds((prev) => {
      const next = new Set(prev);
      next.add(studentId);
      return next;
    });
  };

  const handleMarkAll = (status: AttendanceStatus) => {
    if (!dashboard) return;
    const nextDraft: DraftState = {};
    const nextTouched = new Set<string>();
    dashboard.submissions.forEach((sub) => {
      nextDraft[sub.studentId] = status;
      nextTouched.add(sub.studentId);
    });
    setDraft(nextDraft);
    setTouchedStudentIds(nextTouched);
    setStatusMessage({ type: 'info', text: `All students marked ${status.toUpperCase()}` });
  };

  const handleReset = () => {
    if (!dashboard) return;
    setDraft(getInitialDraft(dashboard.submissions));
    setTouchedStudentIds(new Set());
    setStatusMessage({ type: 'info', text: 'Reset all changes to saved state' });
  };

  const handleSaveDraft = async () => {
    if (!dashboard?.classId) return;
    const payload = buildPayload(submissions, draft);
    if (!payload.length) return;
    await saveDraftMutation.mutateAsync(payload);
  };

  const handleSubmit = async () => {
    if (!dashboard?.classId) return;
    const payload = buildPayload(submissions, draft);
    await submitMutation.mutateAsync(payload);
  };

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = window.setTimeout(() => setStatusMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [statusMessage]);

  const formattedDate = useMemo(() => {
    if (!dashboard?.date) return '';
    try {
      return format(parseISO(dashboard.date), 'EEEE, MMMM d, yyyy');
    } catch {
      return dashboard.date;
    }
  }, [dashboard?.date]);

  if (isLoading) {
    return (
      <DashboardShell title="Today's Attendance" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-6 w-48 bg-slate-200 rounded-lg mx-auto" />
            <div className="h-4 w-64 bg-slate-100 rounded-lg mx-auto" />
          </div>
          <p className="text-sm font-medium text-slate-500 mt-4">Loading homeroom attendance roster…</p>
        </div>
      </DashboardShell>
    );
  }

  if (isError) {
    return (
      <DashboardShell title="Today's Attendance" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-rose-100 shadow-xs text-center space-y-3">
          <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto text-lg font-bold">✕</div>
          <h3 className="text-base font-bold text-slate-900">Failed to load dashboard</h3>
          <p className="text-sm text-rose-600">{(error as Error).message}</p>
        </div>
      </DashboardShell>
    );
  }

  if (!dashboard?.classId || dashboard.attendanceStatus === 'no-class') {
    return (
      <DashboardShell title="Today's Attendance" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto text-xl font-bold border border-indigo-100">🏛</div>
          <h3 className="text-lg font-bold text-slate-900">No Homeroom Assigned</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {dashboard?.assignedClasses?.length
              ? 'No active attendance roster scheduled for your class today.'
              : 'You do not have a homeroom class assigned. Please contact the school office.'}
          </p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Today's Attendance" role="teacher">
      <div className="space-y-6 pb-20 md:pb-6">
        {/* Status Toast Notification */}
        {statusMessage && (
          <div
            className={clsx(
              'px-4 py-3 rounded-2xl text-sm font-semibold flex items-center justify-between border shadow-sm transition-all',
              statusMessage.type === 'success' && 'bg-teal-50 text-teal-800 border-teal-200',
              statusMessage.type === 'info' && 'bg-indigo-50 text-indigo-800 border-indigo-200',
              statusMessage.type === 'error' && 'bg-rose-50 text-rose-800 border-rose-200',
            )}
          >
            <div className="flex items-center gap-2">
              <span>{statusMessage.type === 'success' ? '✓' : statusMessage.type === 'error' ? '✕' : 'ℹ'}</span>
              <span>{statusMessage.text}</span>
            </div>
            <button
              type="button"
              onClick={() => setStatusMessage(null)}
              className="text-xs font-bold opacity-60 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ======================================================== */}
        {/* TOP SECTION: Class Snapshot & Live Summary (V1 on Desktop) */}
        {/* ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Class Card */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3 flex flex-col justify-between">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Class Homeroom</span>
                <span className="text-[11px] font-bold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full border border-indigo-100">
                  Active
                </span>
              </div>
              {dashboard.assignedClasses && dashboard.assignedClasses.length > 1 ? (
                <div className="pt-1">
                  <select
                    value={selectedClassId || dashboard.classId || ''}
                    onChange={(e) => setSelectedClassId(e.target.value)}
                    className="w-full bg-slate-50 rounded-xl border border-slate-200 px-3 py-2 text-base font-bold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    {dashboard.assignedClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <h3 className="text-xl font-black text-slate-900">{dashboard.className}</h3>
              )}
              <p className="text-xs text-slate-500 font-medium">
                {formattedDate} • {submissions.length} Students
              </p>
            </div>
            {dashboard.quickActions?.lastSubmittedAt && (
              <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-400">
                Last submitted: {new Date(dashboard.quickActions.lastSubmittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>

          {/* Live Summary & Action Bar (Desktop + Tablet) */}
          <div className="lg:col-span-3 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3.5">
              <div className="flex items-center gap-2.5">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Live Class Summary</span>
                <span className="text-xs font-black text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
                  {totals.rate}% Present
                </span>
                {hasChanges && (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    Unsaved changes
                  </span>
                )}
              </div>

              {/* Action Buttons (Desktop ≥md) */}
              <div className="hidden md:flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleMarkAll('present')}
                  disabled={!submissions.length}
                  className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl border border-indigo-200 transition active:scale-95 disabled:opacity-50"
                >
                  Mark All Present
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!submissions.length || !hasChanges}
                  className="px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-semibold text-xs rounded-xl border border-slate-200 transition disabled:opacity-40"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={!dashboard.classId || saveDraftMutation.isPending || touchedStudentIds.size === 0}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition active:scale-95 disabled:opacity-40"
                >
                  {saveDraftMutation.isPending ? 'Saving…' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!dashboard.classId || submitMutation.isPending}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition active:scale-95 disabled:opacity-50"
                >
                  {submitMutation.isPending ? 'Submitting…' : 'Submit Attendance'}
                </button>
              </div>

              {/* Mobile Quick Action for top */}
              <div className="md:hidden flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleMarkAll('present')}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 font-bold text-xs rounded-xl border border-indigo-200"
                >
                  All Present
                </button>
              </div>
            </div>

            {/* 4 Tactile Soft Counter Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-teal-50/70 border border-teal-100 p-3 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-teal-800">Present</div>
                  <div className="text-2xl font-black text-teal-700">{totals.present}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-teal-100/60 text-teal-700 flex items-center justify-center font-bold text-sm">
                  ✓
                </span>
              </div>
              <div className="bg-rose-50/70 border border-rose-100 p-3 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-rose-800">Absent</div>
                  <div className="text-2xl font-black text-rose-700">{totals.absent}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-rose-100/60 text-rose-700 flex items-center justify-center font-bold text-sm">
                  ✕
                </span>
              </div>
              <div className="bg-amber-50/70 border border-amber-100 p-3 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Late</div>
                  <div className="text-2xl font-black text-amber-700">{totals.late}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-amber-100/60 text-amber-700 flex items-center justify-center font-bold text-sm">
                  ⏱
                </span>
              </div>
              <div className="bg-slate-100 border border-slate-200 p-3 rounded-2xl flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">On Leave</div>
                  <div className="text-2xl font-black text-slate-800">{totals.leave}</div>
                </div>
                <span className="w-8 h-8 rounded-full bg-slate-200/70 text-slate-700 flex items-center justify-center font-bold text-sm">
                  📝
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ======================================================== */}
        {/* DESKTOP VIEW (≥md): V1 Compact Mark-First Roster Table */}
        {/* ======================================================== */}
        <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
              <h4 className="font-bold text-sm text-slate-900">Student Roll Call Roster</h4>
              <span className="text-xs font-semibold text-slate-500">({submissions.length} Students)</span>
            </div>
            <div className="text-xs text-slate-500 font-medium">
              Click any status pill to mark attendance
            </div>
          </div>
          <div className="overflow-x-auto max-h-[560px]">
            <table className="min-w-full divide-y divide-slate-200 text-xs">
              <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left">Roll #</th>
                  <th className="px-4 py-3 text-left">Student Name</th>
                  <th className="px-4 py-3 text-left">Current Status</th>
                  <th className="px-4 py-3 text-left">State</th>
                  <th className="px-4 py-3 text-right">Quick Mark Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {submissions.map((submission) => {
                  const currentStatus = draft[submission.studentId] ?? submission.status;
                  return (
                    <tr key={submission.studentId} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-4 font-bold font-mono text-sm sm:text-base text-slate-900">{submission.rollNumber}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3.5 min-w-0">
                          {submission.photo ? (
                            <div className="w-14 h-14 rounded-2xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100 ring-1 ring-slate-200 shadow-xs">
                              <img
                                src={resolveUserPhoto(submission.photo, 'student')}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <span className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-700 font-mono text-sm font-black flex items-center justify-center border border-indigo-100 shrink-0">
                              {submission.rollNumber ? submission.rollNumber.slice(-2) : '—'}
                            </span>
                          )}
                          <span className="font-bold text-base lg:text-lg text-slate-900 truncate">{submission.studentName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={clsx(
                            'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border',
                            currentStatus === 'present' && 'bg-teal-50 text-teal-700 border-teal-200',
                            currentStatus === 'absent' && 'bg-rose-50 text-rose-700 border-rose-200',
                            currentStatus === 'late' && 'bg-amber-50 text-amber-700 border-amber-200',
                            currentStatus === 'leave' && 'bg-slate-100 text-slate-700 border-slate-200',
                          )}
                        >
                          {currentStatus.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-400 font-medium">
                        {submission.hasRecord ? 'Submitted' : submission.isDraft ? 'Draft' : 'Not marked'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200 gap-1">
                          <button
                            type="button"
                            onClick={() => handleSetStatus(submission.studentId, 'present')}
                            className={clsx(
                              'px-3.5 py-2 rounded-xl text-xs font-bold transition-all min-h-[38px] sm:min-h-[40px]',
                              currentStatus === 'present'
                                ? 'bg-teal-600 text-white font-extrabold shadow-xs'
                                : 'text-slate-600 hover:bg-white',
                            )}
                          >
                            P
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSetStatus(submission.studentId, 'absent')}
                            className={clsx(
                              'px-3.5 py-2 rounded-xl text-xs font-bold transition-all min-h-[38px] sm:min-h-[40px]',
                              currentStatus === 'absent'
                                ? 'bg-rose-600 text-white font-extrabold shadow-xs'
                                : 'text-slate-600 hover:bg-white',
                            )}
                          >
                            A
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSetStatus(submission.studentId, 'late')}
                            className={clsx(
                              'px-3.5 py-2 rounded-xl text-xs font-bold transition-all min-h-[38px] sm:min-h-[40px]',
                              currentStatus === 'late'
                                ? 'bg-amber-600 text-white font-extrabold shadow-xs'
                                : 'text-slate-600 hover:bg-white',
                            )}
                          >
                            L
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSetStatus(submission.studentId, 'leave')}
                            className={clsx(
                              'px-3.5 py-2 rounded-xl text-xs font-bold transition-all min-h-[38px] sm:min-h-[40px]',
                              currentStatus === 'leave'
                                ? 'bg-slate-600 text-white font-extrabold shadow-xs'
                                : 'text-slate-600 hover:bg-white',
                            )}
                          >
                            Lv
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!submissions.length && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                      No students enrolled in this homeroom yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ======================================================== */}
        {/* MOBILE VIEW (<md): Soft Academic Segmented Switch UX */}
        {/* ======================================================== */}
        <div className="md:hidden space-y-3">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">
            Student Roster ({submissions.length})
          </div>
          <div className="space-y-2.5">
            {submissions.map((submission) => {
              const currentStatus = draft[submission.studentId] ?? submission.status;
              const rollShort = submission.rollNumber ? submission.rollNumber.slice(-2) : '—';
              return (
                <div
                  key={submission.studentId}
                  className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3.5 min-w-0">
                      {submission.photo ? (
                        <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100 ring-1 ring-slate-200 shadow-xs">
                          <img
                            src={resolveUserPhoto(submission.photo, 'student')}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <span className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-700 font-mono text-base font-black flex items-center justify-center border border-indigo-100 shrink-0">
                          {rollShort}
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="text-base sm:text-lg font-bold text-slate-900 leading-tight truncate">{submission.studentName}</div>
                        <div className="text-xs sm:text-sm text-slate-500 font-mono font-medium mt-0.5">Roll #{submission.rollNumber}</div>
                      </div>
                    </div>
                    <span
                      className={clsx(
                        'text-xs font-bold uppercase px-3 py-1 rounded-full border shrink-0',
                        currentStatus === 'present' && 'bg-teal-50 text-teal-700 border-teal-200',
                        currentStatus === 'absent' && 'bg-rose-50 text-rose-700 border-rose-200',
                        currentStatus === 'late' && 'bg-amber-50 text-amber-700 border-amber-200',
                        currentStatus === 'leave' && 'bg-slate-100 text-slate-700 border-slate-200',
                      )}
                    >
                      {currentStatus}
                    </span>
                  </div>

                  {/* Soft Segmented Status Switch (≥40px Touch Target) */}
                  <div className="grid grid-cols-4 p-1 bg-slate-100 rounded-xl gap-1 text-xs text-center">
                    <button
                      type="button"
                      onClick={() => handleSetStatus(submission.studentId, 'present')}
                      className={clsx(
                        'py-2.5 rounded-xl font-extrabold text-sm min-h-[44px] transition-all',
                        currentStatus === 'present'
                          ? 'bg-teal-600 text-white shadow-xs'
                          : 'text-slate-600 hover:bg-white',
                      )}
                    >
                      P
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetStatus(submission.studentId, 'absent')}
                      className={clsx(
                        'py-2.5 rounded-xl font-extrabold text-sm min-h-[44px] transition-all',
                        currentStatus === 'absent'
                          ? 'bg-rose-600 text-white shadow-xs'
                          : 'text-slate-600 hover:bg-white',
                      )}
                    >
                      A
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetStatus(submission.studentId, 'late')}
                      className={clsx(
                        'py-2.5 rounded-xl font-extrabold text-sm min-h-[44px] transition-all',
                        currentStatus === 'late'
                          ? 'bg-amber-600 text-white shadow-xs'
                          : 'text-slate-600 hover:bg-white',
                      )}
                    >
                      L
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetStatus(submission.studentId, 'leave')}
                      className={clsx(
                        'py-2.5 rounded-xl font-extrabold text-sm min-h-[44px] transition-all',
                        currentStatus === 'leave'
                          ? 'bg-slate-600 text-white shadow-xs'
                          : 'text-slate-600 hover:bg-white',
                      )}
                    >
                      Lv
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Sticky Save / Submit Bar (<md) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 shadow-lg z-30 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] text-slate-400 font-bold uppercase">Roster</div>
            <div className="text-xs font-black text-indigo-700">
              {totals.present}/{totals.total} ({totals.rate}%)
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!dashboard.classId || saveDraftMutation.isPending || touchedStudentIds.size === 0}
              className="bg-slate-100 active:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-2.5 rounded-xl border border-slate-200 min-h-[42px] disabled:opacity-40"
            >
              Draft
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!dashboard.classId || submitMutation.isPending}
              className="bg-indigo-600 active:bg-indigo-700 text-white text-xs font-black px-4 py-2.5 rounded-xl shadow-xs min-h-[42px] flex items-center gap-1 disabled:opacity-50"
            >
              <span>{submitMutation.isPending ? 'Submitting…' : 'Submit'}</span>
              <span>✓</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* MONTHLY ATTENDANCE TREND CHART (Soft Academic Restyle)   */}
        {/* ======================================================== */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h4 className="font-bold text-sm text-slate-900">Monthly Attendance Trend</h4>
              <p className="text-xs text-slate-500">Historical performance for {dashboard.className}</p>
            </div>
            <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100">
              6-Month Trend
            </span>
          </div>

          {isTrendLoading ? (
            <p className="text-xs text-slate-400 py-8 text-center">Loading trend data…</p>
          ) : !attendanceTrend?.months?.length ? (
            <p className="text-xs text-slate-400 py-8 text-center">No historical attendance trend data available yet.</p>
          ) : (
            <div className="h-64 pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={attendanceTrend.months} margin={{ top: 10, right: 16, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                  />
                  <Tooltip formatter={(value: number | string, name) => (name === 'Attendance %' ? `${value}%` : value)} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="present" name="Present" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="left" type="monotone" dataKey="absent" name="Absent" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="rate"
                    name="Attendance %"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
